import { createSlice } from '@reduxjs/toolkit'
import { MasterDataState } from './types'
import { MasterDataService } from '../services/MasterDataService'
import { transformMasterData } from '../../../utils/transformMasterData'

const initialState: MasterDataState = {
  // State
  isGetAllCountriesLoading: false,
  isGetAllRegionsLoading: false,
  isGetAllProvincesLoading: false,
  isGetAllDistrictsLoading: false,
  isGetAllSubDistrictsLoading: false,
  isGetAllNationalitiesLoading: false,
  isGetAllPrefixesLoading: false,
  isGetAllBeneficiariesLoading: false,
  isGetAllFlightsLoading: false,
  isGetOccupationsMasterDataLoading: false,
  isGetDealerMasterDataLoading: false,
  // Fetched
  regions: [],
  countries: [],
  provinces: [],
  districts: [],
  subDistricts: [],
  nationalities: [],
  prefixes: [],
  beneficiaries: [],
  flights: [],
  occupations: [],
  dealers: []
}

const slice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    resetCountries: (state) => {
      state.countries = []
    }
  },
  extraReducers: (builder) => {
    builder.addCase(MasterDataService.getAllRegionsAsyncThunk.pending, (state) => {
      state.isGetAllRegionsLoading = true
    })
    builder.addCase(MasterDataService.getAllRegionsAsyncThunk.rejected, (state) => {
      state.isGetAllRegionsLoading = false
    })
    builder.addCase(MasterDataService.getAllRegionsAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllRegionsLoading = false
      state.regions = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllCountriesAsyncThunk.pending, (state) => {
      state.isGetAllCountriesLoading = true
    })
    builder.addCase(MasterDataService.getAllCountriesAsyncThunk.rejected, (state) => {
      state.isGetAllCountriesLoading = false
    })
    builder.addCase(MasterDataService.getAllCountriesAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllCountriesLoading = false
      state.countries = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllProvinceMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllProvincesLoading = true
    })
    builder.addCase(MasterDataService.getAllProvinceMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllProvincesLoading = false
    })
    builder.addCase(MasterDataService.getAllProvinceMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllProvincesLoading = false
      state.provinces = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllDistrictMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllDistrictsLoading = true
    })
    builder.addCase(MasterDataService.getAllDistrictMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllDistrictsLoading = false
    })
    builder.addCase(MasterDataService.getAllDistrictMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllDistrictsLoading = false
      state.districts = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllSubDistrictMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllSubDistrictsLoading = true
    })
    builder.addCase(MasterDataService.getAllSubDistrictMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllSubDistrictsLoading = false
    })
    builder.addCase(MasterDataService.getAllSubDistrictMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllSubDistrictsLoading = false
      state.subDistricts = action.payload.map((data) => ({
        id: data.id,
        label: data.name,
        postalCode: data.postalCode
      }))
    })
    builder.addCase(MasterDataService.getAllNationalitiesMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllNationalitiesLoading = true
    })
    builder.addCase(MasterDataService.getAllNationalitiesMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllNationalitiesLoading = false
    })
    builder.addCase(MasterDataService.getAllNationalitiesMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllNationalitiesLoading = false
      state.nationalities = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllPrefixesMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllPrefixesLoading = true
    })
    builder.addCase(MasterDataService.getAllPrefixesMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllPrefixesLoading = false
    })
    builder.addCase(MasterDataService.getAllPrefixesMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllPrefixesLoading = false
      state.prefixes = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllBeneficiariesMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllBeneficiariesLoading = true
    })
    builder.addCase(MasterDataService.getAllBeneficiariesMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllBeneficiariesLoading = false
    })
    builder.addCase(MasterDataService.getAllBeneficiariesMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllBeneficiariesLoading = false
      state.beneficiaries = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getAllFlightsMasterDataAsyncThunk.pending, (state) => {
      state.isGetAllFlightsLoading = true
    })
    builder.addCase(MasterDataService.getAllFlightsMasterDataAsyncThunk.rejected, (state) => {
      state.isGetAllFlightsLoading = false
    })
    builder.addCase(MasterDataService.getAllFlightsMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetAllFlightsLoading = false
      state.flights = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getOccupationsMasterDataAsyncThunk.pending, (state) => {
      state.isGetOccupationsMasterDataLoading = true
    })
    builder.addCase(MasterDataService.getOccupationsMasterDataAsyncThunk.rejected, (state) => {
      state.isGetOccupationsMasterDataLoading = false
    })
    builder.addCase(MasterDataService.getOccupationsMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetOccupationsMasterDataLoading = false
      state.occupations = transformMasterData(action.payload)
    })
    builder.addCase(MasterDataService.getDealerMasterDataAsyncThunk.pending, (state) => {
      state.isGetDealerMasterDataLoading = true
    })
    builder.addCase(MasterDataService.getDealerMasterDataAsyncThunk.rejected, (state) => {
      state.isGetDealerMasterDataLoading = false
    })
    builder.addCase(MasterDataService.getDealerMasterDataAsyncThunk.fulfilled, (state, action) => {
      state.isGetDealerMasterDataLoading = false
      state.dealers = transformMasterData(action.payload)
    })
  }
})

export const { resetCountries } = slice.actions
export default slice.reducer
